import { useEffect, useMemo, useState } from 'react';
import {
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  Radio,
  FormControlLabel,
  RadioGroup,
  Button,
  Typography,
} from '@mui/material';
import CustomFormControlForSelect from './CustomFormControlForSelect';
import { PrepPopModal } from '../../components/Questions/PrepPopModal';
('../../components/Questions/PrePopModal');
import {
  confirmData,
  QuestionConfirmData,
} from '../../components/Questions/QuestionConfirmData';

import {
  MobileStateAnswer,
  MobileStateQuestion,
  SaveDataAnswerGroup,
  SaveDataAnswerType,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import CustomTypography from './CustomTypography';
import { debounce } from '../../utils';
import { Platform } from '../../constants/platform';

interface SingleSelectProps {
  question: MobileStateQuestion;
  answers: SaveDataAnswerGroup | undefined;
  disabled?: boolean;
  stringMapper: (key: string) => string;
  onSetAnswer: (questionKey: string, answers: string[]) => void;
}

interface SelectorProps {
  label: string;
  value: string;
  disabled?: boolean;
  options: MobileStateAnswer[];
  onChange: (event: SelectChangeEvent<string>) => void;
  stringMapper: (value: string) => string;
}

const CustomSingleSelect = ({
  question,
  answers,
  disabled,
  stringMapper,
  onSetAnswer,
}: SingleSelectProps) => {
  const options = question?.Answers || [];
  const [userAnswer, setUserAnswer] = useState<string>('');
  const [confirmAnswer, setConfirmAnswer] = useState<string | undefined>();
  const [visible, setVisible] = useState<boolean>(false);

  const setAnswer = debounce((answer: string) => {
    if (answers?.AnswerType === SaveDataAnswerType.PrePop) {
      setVisible(false);
      setConfirmAnswer(answer);
    } else {
      setUserAnswer(answer);
      setVisible(false);
      if (onSetAnswer) onSetAnswer(question.QuestionKey, [answer]);
    }
  }, 100);

  const label = useMemo(() => {
    if (stringMapper) {
      return stringMapper(question?.Title);
    }
    return question?.Title;
  }, [question, stringMapper]);

  useEffect(() => {
    const newAnswers: string[] = [];
    if (answers !== undefined) {
      for (const item of answers.Answers) {
        if (item?.Active) {
          newAnswers.push(item?.Answer);
        }
      }
    }
    setUserAnswer(newAnswers[0]);
  }, [answers]);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    if (disabled) return;
    const answer = event?.target?.value;
    setAnswer(answer);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1',
      }}
    >
      {options.length > 5 ? (
        <DropDownSelector
          label={label}
          value={userAnswer}
          disabled={disabled}
          onChange={handleSelectChange}
          options={options}
          stringMapper={stringMapper}
        />
      ) : (
        <RadioButtonRow
          label={label}
          value={userAnswer}
          disabled={disabled}
          onChange={handleSelectChange}
          options={options}
          stringMapper={stringMapper}
        />
      )}

      {confirmAnswer && (
        <PrepPopModal
          visible={confirmAnswer !== undefined}
          hide={() => {
            setConfirmAnswer(undefined);
          }}
        >
          <Typography variant='h6' sx={{ mb: 1 }}>
            {confirmData?.title} to {confirmAnswer.toLanguagePack()}
          </Typography>
          <Typography variant='body1' sx={{ mb: 2 }}>
            {confirmData?.message}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant='outlined'
              fullWidth
              onClick={() => {
                setConfirmAnswer(undefined);
              }}
            >
              {confirmData?.cancelText}
            </Button>
            <Button
              variant='contained'
              fullWidth
              onClick={() => {
                if (confirmAnswer !== undefined && onSetAnswer) {
                  onSetAnswer(question.QuestionKey, [confirmAnswer]);
                  setConfirmAnswer(undefined);
                  setVisible(false);
                }
              }}
            >
              {confirmData?.successText}
            </Button>
          </Box>
        </PrepPopModal>
      )}
    </Box>
  );
};
const DropDownSelector = ({
  label,
  value,
  options,
  disabled,
  onChange,
  stringMapper,
}: SelectorProps) => {
  return (
    <CustomFormControlForSelect label={''} error={false} helperText={''}>
      {Platform?.isAudit() && (
        <CustomTypography
          variant='h5'
          sx={{
            color: '#343330',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: 'normal',
            marginBottom: '0.5rem',
          }}
        >
          {label}
        </CustomTypography>
      )}
      <Select
        disabled={disabled}
        value={value ?? ''}
        onChange={onChange}
        fullWidth
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return <em>Select Value</em>; // Custom placeholder appearance
          }
          return stringMapper(selected);
        }}
      >
        {options?.map((option, index) => (
          <MenuItem key={index} value={option?.TextKey}>
            {stringMapper ? stringMapper(option?.TextKey) : option?.TextKey}
          </MenuItem>
        ))}
      </Select>
    </CustomFormControlForSelect>
  );
};

const RadioButtonRow = ({
  value,
  options,
  disabled,
  onChange,
  stringMapper,
}: SelectorProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        borderRadius: '8px',
        justifyContent: 'flex-start',
        flex: '1',
      }}
    >
      <RadioGroup
        value={value ?? ''}
        row
        onChange={onChange}
        sx={{ justifyContent: 'center' }}
      >
        {options?.map((item: any, index: any) => (
          <FormControlLabel
            key={index}
            value={item?.TextKey}
            control={
              <Radio
                disabled={disabled}
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: 18, // Smaller radio button size
                  },
                }}
              />
            }
            label={
              stringMapper ? stringMapper(item?.TextKey) : item?.TextKey || ''
            }
            sx={{
              margin: '0 10px',
              display: 'flex',
              flex: '1',
              // width: '100px',
              border: '1px solid #E3E3E3',
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
              //  boxShadow: false === 'yes' ? '0 0 5px rgba(0, 0, 0, 0.1)' : 'none',
              '& .MuiTypography-root': {
                color: '#343330',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                fontWeight: '400',
                lineHeight: '20px',
                paddingRight: '0.6rem',
              },
            }}
          />
        ))}
      </RadioGroup>
    </Box>
  );
};

export default CustomSingleSelect;
